<template>
  <div class="zoomable-image">
    <div
      class="zoomable-image__viewport"
      :style="{ height }"
      @wheel.prevent="onWheel"
      @mousedown="startPan"
      @dblclick="reset"
    >
      <img v-if="src" :src="src" draggable="false" :style="imageStyle" />
    </div>
    <div class="zoomable-image__controls">
      <v-btn icon size="small" variant="text" @click="rotateLeft">
        <v-icon>mdi-rotate-left</v-icon>
      </v-btn>
      <v-btn icon size="small" variant="text" @click="zoomOut">
        <v-icon>mdi-magnify-minus-outline</v-icon>
      </v-btn>
      <v-btn icon size="small" variant="text" @click="reset">
        <v-icon>mdi-backup-restore</v-icon>
      </v-btn>
      <v-btn icon size="small" variant="text" @click="zoomIn">
        <v-icon>mdi-magnify-plus-outline</v-icon>
      </v-btn>
      <v-btn icon size="small" variant="text" @click="rotateRight">
        <v-icon>mdi-rotate-right</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
const MIN_SCALE = 1;
const MAX_SCALE = 4;
const SCALE_STEP = 0.25;

// Self-contained zoom/pan/rotate image viewer — mouse wheel to zoom,
// click-and-drag to pan once zoomed in, double-click to reset, plus explicit
// buttons (including 90-degree rotate either direction) for touch/
// discoverability. Shared by the patient-facing upload review page
// (SecureUpload.vue) and the staff-facing document review dialog
// (DocumentExtractionDialog.vue) so both get the same behavior for free.
export default {
  name: "ZoomableImage",
  props: {
    src: { type: String, default: "" },
    height: { type: String, default: "320px" },
  },
  data() {
    return {
      scale: MIN_SCALE,
      x: 0,
      y: 0,
      rotation: 0, // degrees, 0/90/180/270
      panState: null, // { startX, startY, originX, originY }
    };
  },
  computed: {
    imageStyle() {
      return {
        transform: `translate(${this.x}px, ${this.y}px) rotate(${this.rotation}deg) scale(${this.scale})`,
        cursor: this.scale > MIN_SCALE ? "grab" : "default",
      };
    },
  },
  watch: {
    src() {
      this.reset();
    },
  },
  beforeUnmount() {
    this.stopPanListeners();
  },
  methods: {
    clampPan() {
      if (this.scale <= MIN_SCALE) {
        this.x = 0;
        this.y = 0;
      }
    },
    zoomIn() {
      this.scale = Math.min(MAX_SCALE, this.scale + SCALE_STEP);
      this.clampPan();
    },
    zoomOut() {
      this.scale = Math.max(MIN_SCALE, this.scale - SCALE_STEP);
      this.clampPan();
    },
    reset() {
      this.scale = MIN_SCALE;
      this.x = 0;
      this.y = 0;
      this.rotation = 0;
    },
    rotateLeft() {
      this.rotation = (this.rotation - 90 + 360) % 360;
    },
    rotateRight() {
      this.rotation = (this.rotation + 90) % 360;
    },
    onWheel(event) {
      if (event.deltaY < 0) this.zoomIn();
      else this.zoomOut();
    },
    startPan(event) {
      if (this.scale <= MIN_SCALE) return;
      this.panState = {
        startX: event.clientX,
        startY: event.clientY,
        originX: this.x,
        originY: this.y,
      };
      window.addEventListener("mousemove", this.onPan);
      window.addEventListener("mouseup", this.endPan);
    },
    onPan(event) {
      if (!this.panState) return;
      const { startX, startY, originX, originY } = this.panState;
      this.x = originX + (event.clientX - startX);
      this.y = originY + (event.clientY - startY);
    },
    endPan() {
      this.stopPanListeners();
      this.panState = null;
    },
    stopPanListeners() {
      window.removeEventListener("mousemove", this.onPan);
      window.removeEventListener("mouseup", this.endPan);
    },
  },
};
</script>

<style scoped>
.zoomable-image__viewport {
  width: 100%;
  overflow: hidden;
  border-radius: 8px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: none;
  user-select: none;
}

.zoomable-image__viewport img {
  max-width: 100%;
  max-height: 100%;
  transition: transform 0.05s linear;
}

.zoomable-image__controls {
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-top: 4px;
}
</style>
